import { useState, useEffect, useMemo, useRef, memo } from 'react';
import axios from 'axios';
import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { MediaLightbox } from "@/components/ui/media-lightbox";
import { Tabs, TabsContent as SubTabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Plus, Check, X, Edit2, Clock, CheckCircle2, XCircle, AlertCircle, Trash2, RotateCcw } from 'lucide-react';
import { toast } from "sonner";
import ActivityReport from './ActivityReport';
import { OperationDetailViewer } from './OperasyonTab';

function isCanceledError(error) {
  if (!error) return false;
  if (axios.isCancel?.(error)) return true;
  if (error.code === "ERR_CANCELED") return true;
  if (typeof error.message === "string" && error.message.toLowerCase() === "canceled") return true;
  return false;
}
import { formatTurkeyTime } from '../../utils/dateUtils';
import { useRoleFlags } from '@/auth/useRoleFlags';
import { mediaUrl } from '@/utils/mediaUrl';

// Faaliyet tipleri
const ACTIVITY_TYPES = [
  "Çim Biçimi",
  "Form ve Budama",
  "Bölge Geneli Mobil Temizlik",
  "Diğer"
];

// Gün isimleri
const DAY_NAMES = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

// Faaliyet planında seçilebilecek alan türleri (ayrı ayrı)
const LOCATION_TYPES = [
  { id: "park", name: "Park" },
  { id: "pasif", name: "Pasif Alan (Kayıtlı)" },
  { id: "protokol", name: "Protokollü Park" },
  { id: "kayitsiz_pasif", name: "Kayıtsız Pasif Alan" },
  { id: "bolge_disi", name: "Bölge Dışı" },
];

// Bir aktivite öğesinin alan türünü belirle (geçici _loc veya park_id'den türet)
function getLocItemType(item) {
  if (item._loc) return item._loc;
  if (!item.park_id) return "";
  if (item.park_id === "KAYITSIZ_PASIF") return "kayitsiz_pasif";
  if (item.park_id === "BOLGE_DISI") return "bolge_disi";
  if (String(item.park_id).startsWith("protokol:")) return "protokol";
  if (String(item.park_id).startsWith("pasif:")) return "pasif";
  return "park";
}

// Alan ölçüsü metni ("Toplam: 2715 m² · Çim: 1507 m²")
function formatAreaInfo(item) {
  const parts = [];
  if (item.area != null && item.area !== "") parts.push(`Toplam: ${item.area} m²`);
  if (item.grass_area != null && item.grass_area !== "") parts.push(`Çim: ${item.grass_area} m²`);
  return parts.join(" · ");
}

// Bir günün ÇİM BİÇİMİ faaliyetleri için alan toplamı.
// - Yalnızca "Çim Biçimi" faaliyetleri sayılır (form-budama vb. hariç).
// - Aynı yer (park) birden çok faaliyette geçse bile bir kez sayılır (çift sayım yok).
function computeCimBicimiTotals(items) {
  const seen = new Map();
  for (const it of items || []) {
    if (it.activity_type !== "Çim Biçimi") continue;
    // Sentinel park_id'ler (kayıtsız/bölge dışı vb.) farklı yerleri temsil
    // edebilir; bunları park_name ile ayırt et, aksi halde gerçek park_id benzersiz.
    const key = (it.park_id && !AREA_SENTINELS.has(it.park_id))
      ? it.park_id
      : `${it.park_id || ""}|${it.park_name || ""}`;
    if ((!it.park_id && !it.park_name) || seen.has(key)) continue;
    seen.set(key, { area: Number(it.area) || 0, grass: Number(it.grass_area) || 0 });
  }
  let area = 0, grass = 0;
  for (const v of seen.values()) { area += v.area; grass += v.grass; }
  return { area, grass, count: seen.size };
}

const PLAN_TIME_PRESETS = [
  { value: "this_week", label: "Bu Hafta" },
  { value: "next_week", label: "Gelecek Hafta" },
  { value: "following_week", label: "Sonraki Hafta" },
  { value: "all", label: "Tümü" }
];

function toLocalDateOnly(dateStr) {
  if (!dateStr) return null;
  const parts = String(dateStr).split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function getWeekMonday(baseDate) {
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  const day = d.getDay(); // 0 pazar
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d;
}

function addDays(date, days) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

// "YYYY-MM-DD" → "DD.MM.YYYY" (tz kaymasız, string bazlı)
function formatTrDate(dateStr) {
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

// Date → "YYYY-MM-DD" (yerel; toISOString'in UTC kaymasını önler)
function fmtYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Kayıtlı yer olmayan (alan ölçüsü taşımayan) özel park_id değerleri
const AREA_SENTINELS = new Set(["KAYITSIZ_PASIF", "BOLGE_DISI", "PASIF_ALAN", "SERBEST"]);

// Cihaz konumunu al (kamera kaynağında GPS göndermek için). Hata = null.
function getGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });
}

function ActivityManagementTab({
  userRole,
  personnelId,
  personnelName = "",
  parks = [],
  personnel = [],
  API
}) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  // Kullanıcı adını personnel listesinden al (eğer personnelName boşsa)
  const actualPersonnelName = personnelName || personnel.find(p => p.id === personnelId)?.name || "Bilinmiyor";
  const [activeSubTab, setActiveSubTab] = useState("my-plans");
  const [loading, setLoading] = useState(false);
  const [myPlans, setMyPlans] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [editedActivities, setEditedActivities] = useState([]);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, planId: null, fromTab: null });
  const [completionNote, setCompletionNote] = useState("");
  // Faaliyet planında seçilebilecek protokollü parklar ve kayıtlı pasif alanlar
  const [protokolParks, setProtokolParks] = useState([]);
  const [passiveAreas, setPassiveAreas] = useState([]);
  // Admin medya ayarı + tamamlama penceresindeki yüklenen medyalar
  const [mediaSettings, setMediaSettings] = useState({ requirement: "yok", source: "galeri_ve_kamera" });
  const [dialogMedia, setDialogMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [completionSaving, setCompletionSaving] = useState(false);
  // Tam ekran medya görüntüleyici (lightbox)
  const [lightbox, setLightbox] = useState({ open: false, items: [], index: 0 });
  
  const [planTimePreset, setPlanTimePreset] = useState("next_week");
  const [planRangeStart, setPlanRangeStart] = useState("");
  const [planRangeEnd, setPlanRangeEnd] = useState("");
  const [planMultiFormenIds, setPlanMultiFormenIds] = useState([]);
  const [isFormenFilterOpen, setIsFormenFilterOpen] = useState(false);
  const [appliedPlanFilters, setAppliedPlanFilters] = useState({
    timePreset: "next_week",
    rangeStart: "",
    rangeEnd: "",
    formenIds: []
  });
  
  // Haftalık plan formu - Her gün için birden fazla faaliyet olabilir
  const [weekStartDate, setWeekStartDate] = useState("");
  const [selectedFormenId, setSelectedFormenId] = useState(""); // Bölge Müh/Müdür Yard için formen seçimi
  const [selectedFormenName, setSelectedFormenName] = useState("");
  const [activities, setActivities] = useState(
    DAY_NAMES.map((day, index) => ({
      day_name: day,
      date: "",
      items: [{ // Her gün için faaliyet listesi
        activity_type: "",
        park_id: "",
        park_name: "",
        notes: ""
      }]
    }))
  );
  // Plan oluştururken: o haftaya denk gelen operasyon emirleri (tarih → emir[])
  const [createWeekOps, setCreateWeekOps] = useState({});
  // Plan ekranından açılan operasyon emri detayı (salt-okunur)
  const [viewOpId, setViewOpId] = useState(null);
  // Geçmiş haftalarda "Tamamlanamadı" işaretlenmiş ve henüz tekrar yapılmamış
  // faaliyetler — yeni plan oluştururken hatırlatma olarak gösterilir.
  const [carryOverItems, setCarryOverItems] = useState([]);
  // Her devir faaliyeti için seçili gün (key → gün adı)
  const [carryOverDays, setCarryOverDays] = useState({});

  // Plan oluşturma kartında planın sahibi (formen) — seçilen formen yoksa kullanıcının kendisi
  const createOwnerId = (isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenId
    ? selectedFormenId
    : personnelId;

  // Plan oluşturma kartı açıkken sahibin o haftaki operasyon emirlerini çek (gün gün göster)
  useEffect(() => {
    if (!isCreateDialogOpen || !createOwnerId || !weekStartDate) { setCreateWeekOps({}); return; }
    const start = toLocalDateOnly(weekStartDate);
    if (!start) { setCreateWeekOps({}); return; }
    const dates = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
    let alive = true;
    Promise.all(
      dates.map(date =>
        axios.get(`${API}/api/operasyon/by-date`, { params: { personnel_id: createOwnerId, date } })
          .then(r => [date, Array.isArray(r.data) ? r.data : []])
          .catch(() => [date, []])
      )
    ).then(pairs => { if (alive) setCreateWeekOps(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [isCreateDialogOpen, createOwnerId, weekStartDate, API]);

  // Plan oluşturma kartı açıkken: planın sahibinin (formen) ONAYLI geçmiş
  // planlarındaki "Tamamlanamadı" işaretli ve sonradan tekrar yapılmamış
  // faaliyetleri devir listesi olarak getir. Böylece bir sonraki haftayı
  // planlarken yapılamayan parklar görülüp programa eklenebilir.
  useEffect(() => {
    if (!isCreateDialogOpen || !createOwnerId || !weekStartDate) {
      setCarryOverItems([]);
      return;
    }
    const start = toLocalDateOnly(weekStartDate);
    if (!start) { setCarryOverItems([]); return; }
    let alive = true;
    axios.get(`${API}/api/activity-plans`, {
      params: { created_by_id: createOwnerId, status: "approved" }
    })
      .then(r => {
        if (!alive) return;
        const plans = Array.isArray(r.data?.plans) ? r.data.plans : [];
        // Sadece planlanan haftadan ÖNCE başlayan planların işaretli
        // faaliyetlerini topla (tarih sırası — en son durum kazanır).
        const marked = [];
        plans.forEach(p => {
          const ws = toLocalDateOnly(p.week_start_date);
          if (!ws || ws >= start) return;
          (p.activities || []).forEach(a => {
            if (!a || !a.completed_at) return; // sadece işaretlenmiş faaliyetler
            marked.push({ ...a, week_start_date: p.week_start_date, week_end_date: p.week_end_date });
          });
        });
        // En son İŞARETLENEN durum kazanmalı → completed_at zaman damgasına göre
        // sırala (yoksa faaliyet tarihine düş).
        marked.sort((a, b) =>
          new Date(a.completed_at || a.date || 0) - new Date(b.completed_at || b.date || 0)
        );
        // Park + faaliyet tipi bazında en güncel durumu tut; en son
        // "Tamamlanamadı" ise devir listesine girer (sonradan yapıldıysa düşer).
        const latest = new Map();
        marked.forEach(a => {
          const key = `${a.activity_type}__${a.park_id || a.park_name || ""}`;
          latest.set(key, a);
        });
        const carry = [];
        latest.forEach((a, key) => {
          if (a.is_completed === false) carry.push({ ...a, _key: key });
        });
        setCarryOverItems(carry);
      })
      .catch(() => { if (alive) setCarryOverItems([]); });
    return () => { alive = false; };
  }, [isCreateDialogOpen, createOwnerId, weekStartDate, API]);

  // Devir faaliyetini seçilen güne ekle (boş satır varsa onu doldur).
  // Eklenen faaliyet, çift tıklama / karışıklık olmasın diye yukarıdaki
  // devir listesinden çıkarılır; nedeni not olarak öne yazılır.
  const addCarryOverToDay = (item, dayName) => {
    const targetDay = dayName || DAY_NAMES[0];
    const note = `${formatTrDate(item.date)} tarihinde yapılamadı${item.completion_notes ? ` (${item.completion_notes})` : ""} — geçen haftadan devir`;
    setActivities(prev => {
      const next = prev.map(d => ({ ...d, items: d.items.map(it => ({ ...it })) }));
      const dayIdx = next.findIndex(d => d.day_name === targetDay);
      if (dayIdx < 0) return prev;
      const newItem = {
        activity_type: item.activity_type || "",
        park_id: item.park_id || "",
        park_name: item.park_name || "",
        area: item.area ?? null,
        grass_area: item.grass_area ?? null,
        notes: note
      };
      const items = next[dayIdx].items;
      const emptyIdx = items.findIndex(it => !it.activity_type && !it.park_id);
      if (emptyIdx >= 0) items[emptyIdx] = newItem;
      else items.push(newItem);
      return next;
    });
    // Devir listesinden çıkar (tekrar/çift ekleme engeli)
    setCarryOverItems(prev => prev.filter(c => c._key !== item._key));
    setCarryOverDays(prev => {
      const { [item._key]: _omit, ...rest } = prev;
      return rest;
    });
    toast.success(`${item.park_name || item.activity_type} → ${targetDay} gününe eklendi`);
  };

  // Bölge Müh/Müdür Yard için altındaki formenleri listele
  const myFormens = personnel.filter(p => {
    if (isBolgeMuhendisi) return p.bolge_muhendisi_id === personnelId && p.role === "formen";
    if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId && p.role === "formen";
    return false;
  });

  // Sadece kullanıcının kendi parkları (veya seçilen formen'in parkları)
  const myParks = parks.filter(park => {
    if (isAdmin || isMudur || isGolgeAdmin) return true;
    
    // Bölge Müh/Müdür Yard formen seçti mi? O formen'in parklarını göster
    const targetPersonnelId = (isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenId 
      ? selectedFormenId 
      : personnelId;
    
    // Parklarda assigned_personnel_ids var, bunu kontrol et
    if (park.assigned_personnel_ids && park.assigned_personnel_ids.includes(targetPersonnelId)) {
      return true;
    }
    
    // FORMEN için: Altındaki personelin parklarını da göster
    if (isFormen || ((isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenId)) {
      // Altındaki personelleri bul
      const subordinates = personnel.filter(p => p.formen_id === targetPersonnelId);
      const subordinateIds = subordinates.map(s => s.id);
      
      // Parkta bu personellerden biri varsa göster
      if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => subordinateIds.includes(id))) {
        return true;
      }
    }
    
    // Alternatif: formen_id, bolge_muhendisi_id kontrolü
    if (isFormen && park.formen_id === targetPersonnelId) return true;
    if ((isBolgeMuhendisi || selectedFormenId) && park.bolge_muhendisi_id === targetPersonnelId) return true;
    if ((isMudurYardimcisi || selectedFormenId) && park.mudur_yardimcisi_id === targetPersonnelId) return true;

    return false;
  }).sort((a, b) => (a?.name || "").localeCompare((b?.name || ""), "tr-TR", { sensitivity: "base" }));

  // Protokollü park + kayıtlı pasif alan: planın sahibinin (formen/üst) kapsamına göre filtrele
  const _areaScopeId = createOwnerId; // formen seçildiyse o, yoksa kullanıcının kendisi
  const _seesAllAreas = isAdmin || isMudur || isGolgeAdmin;
  const _inScope = (x) =>
    _seesAllAreas ||
    x.assigned_formen_id === _areaScopeId ||
    x.assigned_bolge_muhendisi_id === _areaScopeId ||
    x.assigned_mudur_yardimcisi_id === _areaScopeId;
  const _byName = (a, b) => (a?.name || "").localeCompare(b?.name || "", "tr-TR", { sensitivity: "base" });
  const myProtokolParks = protokolParks.filter(_inScope).sort(_byName);
  const myPassiveAreas = passiveAreas.filter(_inScope).sort(_byName);

  // Bir aktivitenin alan ölçüsünü çöz: kaydedilmiş değer yoksa park_id'den
  // canlı bul (alan ölçüsü kaydı öncesinde oluşturulmuş eski planlar için).
  const resolveActivityArea = (activity) => {
    if (activity.area != null || activity.grass_area != null) {
      return { area: activity.area, grass_area: activity.grass_area };
    }
    const pid = activity.park_id;
    if (!pid) return { area: null, grass_area: null };
    if (String(pid).startsWith("protokol:")) {
      const p = protokolParks.find(x => `protokol:${x.id}` === pid);
      return { area: p?.area ?? null, grass_area: p?.grass_area ?? null };
    }
    if (String(pid).startsWith("pasif:")) {
      const a = passiveAreas.find(x => `pasif:${x.id}` === pid);
      return { area: a?.area ?? null, grass_area: null };
    }
    const park = parks.find(x => x.id === pid);
    return { area: park?.area ?? null, grass_area: park?.grass_area ?? null };
  };

  // Planları yükle
  useEffect(() => {
    if (personnelId) {
      loadMyPlans();
      loadPendingApprovals();
    }
  }, [personnelId, activeSubTab]);

  // Protokollü parkları ve kayıtlı pasif alanları yükle (park seçimi için)
  useEffect(() => {
    let alive = true;
    const pickList = (d) => Array.isArray(d) ? d : (d?.parklar || d?.data || d?.items || d?.passive_areas || []);
    Promise.allSettled([
      axios.get(`${API}/api/protokol-parklar`),
      axios.get(`${API}/api/passive-areas`),
    ]).then(([pRes, aRes]) => {
      if (!alive) return;
      if (pRes.status === "fulfilled") setProtokolParks(pickList(pRes.value.data));
      if (aRes.status === "fulfilled") setPassiveAreas(pickList(aRes.value.data));
    });
    return () => { alive = false; };
  }, [API]);

  // Faaliyet tamamlama medya ayarını yükle (admin tarafından belirlenir)
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/faaliyet/medya-ayarlari`)
      .then(r => {
        if (!alive || !r.data) return;
        setMediaSettings({
          requirement: r.data.requirement || "yok",
          source: r.data.source || "galeri_ve_kamera",
        });
      })
      .catch(() => { /* ayar okunamazsa varsayılan "yok" kalır */ });
    return () => { alive = false; };
  }, [API]);

  // Hızlı "Tamamlandı" tek-tık yolu için çift-gönderim kilidi
  const markingRef = useRef(false);

  // Tamamlama penceresini aç (hem "Tamamlandı" hem "Tamamlanamadı" için)
  const openCompletionDialog = (plan, activity, isCompleted) => {
    const originalIdx = plan.activities.findIndex(a => a === activity);
    if (originalIdx < 0) {
      toast.error("Liste yenilendi, lütfen tekrar deneyin");
      return;
    }
    setSelectedActivity({
      planId: plan.id,
      activityIndex: originalIdx,
      isCompleted,
      parkId: activity.park_id || "",
      parkName: activity.park_name || "",
      activityType: activity.activity_type || "",
      date: activity.date || "",
    });
    setCompletionNote("");
    setDialogMedia([]);
    setShowCompletionDialog(true);
  };

  // Medya ayarını sunucudan taze çek (state'i de günceller, güncel değeri döner)
  const fetchMediaSettings = async () => {
    try {
      const r = await axios.get(`${API}/api/faaliyet/medya-ayarlari`);
      if (r.data) {
        const s = {
          requirement: r.data.requirement || "yok",
          source: r.data.source || "galeri_ve_kamera",
        };
        setMediaSettings(s);
        return s;
      }
    } catch {
      /* ağ hatası: mevcut ayar ile devam */
    }
    return mediaSettings;
  };

  // "Tamamlandı" tıklanınca: medya istenmiyorsa tek tıkla işaretle,
  // isteniyorsa pencere aç (medya/kanıt için).
  const handleMarkCompleted = async (plan, activity) => {
    const originalIdx = plan.activities.findIndex(a => a === activity);
    if (originalIdx < 0) {
      toast.error("Liste yenilendi, lütfen tekrar deneyin");
      return;
    }
    // Ayarı taze çek: admin az önce değiştirdiyse sayfa yenilemeye gerek kalmasın
    const settings = await fetchMediaSettings();
    if (settings.requirement !== "yok") {
      openCompletionDialog(plan, activity, true);
      return;
    }
    if (markingRef.current) return; // çift tık koruması
    markingRef.current = true;
    try {
      await axios.put(`${API}/api/activity-plans/${plan.id}/complete-activity`, {
        activity_index: originalIdx,
        is_completed: true,
      });
      toast.success("Görev tamamlandı olarak işaretlendi");
      await Promise.all([loadMyPlans(), loadPendingApprovals()]);
    } catch (error) {
      if (isCanceledError(error)) return;
      toast.error("Hata: " + (error.response?.data?.detail || error.message));
    } finally {
      markingRef.current = false;
    }
  };

  // Tamamlama işaretini geri al (yanlış işaretleme düzeltme)
  const handleResetActivity = async (plan, activity) => {
    const originalIdx = plan.activities.findIndex(a => a === activity);
    if (originalIdx < 0) {
      toast.error("Liste yenilendi, lütfen tekrar deneyin");
      return;
    }
    if (markingRef.current) return;
    markingRef.current = true;
    try {
      await axios.put(`${API}/api/activity-plans/${plan.id}/reset-activity`, {
        activity_index: originalIdx,
      });
      toast.success("İşaret geri alındı");
      await Promise.all([loadMyPlans(), loadPendingApprovals()]);
    } catch (error) {
      if (isCanceledError(error)) return;
      toast.error("Hata: " + (error.response?.data?.detail || error.message));
    } finally {
      markingRef.current = false;
    }
  };

  // Tamamlama penceresinde foto/video yükle (galeri veya kamera)
  const uploadDialogMedia = async (file, mediaType, source) => {
    if (!file || !selectedActivity) return;
    if (dialogMedia.length >= 3) {
      toast.error("En fazla 3 medya ekleyebilirsiniz");
      return;
    }
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("media_type", mediaType);
      fd.append("source", source);
      if (selectedActivity.parkId) fd.append("park_id", selectedActivity.parkId);
      if (source === "kamera") {
        const pos = await getGeo();
        if (pos) {
          fd.append("latitude", pos.lat);
          fd.append("longitude", pos.lng);
        } else {
          toast("Konum alınamadı; medya konum bilgisi olmadan yüklenecek.");
        }
      }
      const res = await axios.post(
        `${API}/api/activity-plans/${selectedActivity.planId}/upload-media`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res.data?.success) {
        setDialogMedia(prev => [...prev, {
          url: res.data.file_url,
          type: res.data.media_type,
          source: res.data.source,
          uploaded_at: res.data.uploaded_at,
        }]);
      } else {
        toast.error("Medya yüklenemedi");
      }
    } catch (error) {
      if (isCanceledError(error)) return;
      toast.error("Yükleme hatası: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingMedia(false);
    }
  };

  // Tamamlama penceresini onayla (not + medya ile işaretle)
  const submitCompletion = async () => {
    if (!selectedActivity) return;
    const { planId, isCompleted } = selectedActivity;
    if (!isCompleted && !completionNote.trim()) {
      toast.error("Lütfen bir açıklama girin");
      return;
    }
    // Pencere açıkken 15 sn'lik arka plan yenilemesi index'i kaydırmış olabilir;
    // güncel plandan stabil alanlarla (tarih+tip+yer) index'i yeniden bul.
    let activityIndex = selectedActivity.activityIndex;
    const curPlan = myPlans.find(p => p.id === planId) || pendingApprovals.find(p => p.id === planId);
    if (curPlan) {
      const found = curPlan.activities.findIndex(a =>
        a.date === selectedActivity.date &&
        a.activity_type === selectedActivity.activityType &&
        (a.park_id || "") === (selectedActivity.parkId || "")
      );
      if (found >= 0) activityIndex = found;
    }
    // Medya zorunluluğu yalnızca "Tamamlandı" için (backend de doğrular).
    // "Tamamlanamadı"da medya opsiyoneldir.
    if (isCompleted) {
      const req = mediaSettings.requirement;
      const types = new Set(dialogMedia.map(m => m.type));
      if (req === "foto" && !types.has("photo")) { toast.error("En az 1 fotoğraf ekleyin"); return; }
      if (req === "video" && !types.has("video")) { toast.error("En az 1 video ekleyin"); return; }
      if (req === "ikisi" && !(types.has("photo") && types.has("video"))) {
        toast.error("En az 1 fotoğraf ve 1 video ekleyin");
        return;
      }
    }
    setCompletionSaving(true);
    try {
      await axios.put(`${API}/api/activity-plans/${planId}/complete-activity`, {
        activity_index: activityIndex,
        is_completed: isCompleted,
        completion_notes: completionNote,
        media: dialogMedia,
      });
      toast.success(isCompleted ? "Görev tamamlandı olarak işaretlendi" : "Görev tamamlanamadı olarak işaretlendi");
      setShowCompletionDialog(false);
      setDialogMedia([]);
      await Promise.all([loadMyPlans(), loadPendingApprovals()]);
    } catch (error) {
      if (isCanceledError(error)) return;
      toast.error("Hata: " + (error.response?.data?.detail || error.message));
    } finally {
      setCompletionSaving(false);
    }
  };

  const loadMyPlans = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      let plans = [];
      
      if (isFormen) {
        // Formen sadece kendi planlarını görür
        const response = await axios.get(`${API}/api/activity-plans?created_by_id=${personnelId}`);
        if (response.data.success) {
          plans = response.data.plans;
          setMyPlans(plans);
        }
      } else if (isBolgeMuhendisi) {
        // Bölge Mühendisi: Altındaki formenlerin planlarını VE kendi onayladığı planları görür
        const response = await axios.get(`${API}/api/activity-plans`);
        if (response.data.success) {
          const myFormens = personnel.filter(p => 
            p.role === "formen" && p.bolge_muhendisi_id === personnelId
          );
          const formenIds = myFormens.map(f => f.id);
          
          // Kendi oluşturduğu veya onayladığı planlar
          const filteredPlans = response.data.plans.filter(p => 
            formenIds.includes(p.created_by_id) || // Altındaki formenlerin planları
            p.approved_by_bolge_muhendisi_id === personnelId // Kendi onayladıkları
          );
          setMyPlans(filteredPlans);
        }
      } else if (isMudurYardimcisi) {
        // Müdür Yardımcısı: Kendi onayladığı veya onaya gönderdiği planları görür
        const response = await axios.get(`${API}/api/activity-plans`);
        if (response.data.success) {
          const filteredPlans = response.data.plans.filter(p => 
            p.approved_by_mudur_yardimcisi_id === personnelId // Kendi onayladıkları
          );
          setMyPlans(filteredPlans);
        }
      } else if (isAdmin || isMudur || isGolgeAdmin) {
        // Müdür ve Başkan/GKAGÖLGE: tüm planları görür (Başkan sadece görüntüleme)
        const response = await axios.get(`${API}/api/activity-plans`);
        if (response.data.plans) {
          plans = response.data.plans;
          setMyPlans(plans);
        }
      }
      
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan yükleme hatası:", error);
      toast.error("Planlar yüklenemedi");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadPendingApprovals = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      // Rolüme göre bekleyen onayları getir
      let status = "";
      if (isBolgeMuhendisi) status = "pending_bolge";
      else if (isMudurYardimcisi) status = "pending_mudur_yard";
      else if (isAdmin || isMudur || isGolgeAdmin) status = "pending_mudur";
      
      if (status) {
        const response = await axios.get(`${API}/api/activity-plans?status=${status}`);
        if (response.data.success) {
          const plans = response.data.plans || [];
          // Sadece BU kullanıcıya atanmış (current_approver_id) planları göster.
          // Aksi halde her bölge müh./müdür yard. tüm bölgelerin bekleyen
          // planlarını görüp onaylayabilirdi (hiyerarşi ihlali). current_approver_id
          // create/approve akışında ilgili üst yöneticiye set edilir.
          // Admin/Müdür genel gözetim yapar (atama filtresi yok).
          const isOverseer = isAdmin || isMudur || isGolgeAdmin;
          const filtered = isOverseer
            ? plans
            : plans.filter(p => String(p.current_approver_id ?? "") === String(personnelId));
          setPendingApprovals(filtered);
        }
      }
    } catch (error) {
      console.error("Onay listesi yükleme hatası:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Faaliyet Yönetimi açıkken listeleri kısa aralıklarla sessiz yenile
  useEffect(() => {
    if (!personnelId) return;

    const refreshLive = async () => {
      if (document.hidden) return;
      await Promise.all([
        loadMyPlans(true),
        loadPendingApprovals(true)
      ]);
    };

    refreshLive(); // Sekmeye girer girmez ilk canlı senkron
    // PERF-008: 4s → 15s (trafik -%75). `document.hidden` skip + window focus
    // event ile sekme yeniden aktif olduğunda anlık refresh; kullanıcı algılanan
    // canlılık aynı kalır, idle trafik düşer. Tam WebSocket entegrasyonu Sprint 5'e.
    const interval = setInterval(refreshLive, 15000);
    const onFocus = () => refreshLive();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [personnelId, userRole, activeSubTab]);

  // Plan silme fonksiyonu
  const handleDeletePlan = async () => {
    const { planId, fromTab } = deleteConfirmDialog;
    
    try {
      await axios.delete(`${API}/api/activity-plans/${planId}`);
      toast.success("Plan silindi!");
      
      // İlgili listeyi yenile
      if (fromTab === "approvals") {
        await loadPendingApprovals();
      } else {
        await loadMyPlans();
      }
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Silme hatası:", error);
      toast.error("Plan silinemedi: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteConfirmDialog({ open: false, planId: null, fromTab: null });
    }
  };

  // Hafta başlangıç tarihi değiştiğinde günlerin tarihlerini hesapla (tz-güvenli)
  useEffect(() => {
    if (weekStartDate) {
      const startDate = toLocalDateOnly(weekStartDate);
      if (!startDate) return;
      const newActivities = activities.map((act, index) => ({
        ...act,
        date: fmtYmd(addDays(startDate, index)),
      }));
      setActivities(newActivities);
    }
  }, [weekStartDate]);

  const handleCreatePlan = async () => {
    try {
      // Validasyon
      if (!weekStartDate) {
        toast.error("Hafta başlangıç tarihini seçin");
        return;
      }

      // Tüm günlerin faaliyetlerini düzleştir
      const allActivities = [];
      activities.forEach(day => {
        day.items.forEach(item => {
          if (item.activity_type) { // Sadece dolu faaliyetler
            // Park seçilmemiş faaliyetleri kontrol et (Bölge geneli hariç)
            if (item.activity_type !== "Bölge Geneli Mobil Temizlik" && !item.park_id) {
              throw new Error("Lütfen tüm faaliyetler için alan türü ve yer seçin");
            }
            // Kayıtsız pasif alan / bölge dışı seçildiyse açıklama (isim) zorunlu
            if ((item.park_id === "KAYITSIZ_PASIF" || item.park_id === "BOLGE_DISI") && !item.park_name?.trim()) {
              throw new Error("Kayıtsız pasif alan / bölge dışı için bir açıklama (isim) girin");
            }

            allActivities.push({
              date: day.date,
              day_name: day.day_name,
              activity_type: item.activity_type,
              park_id: item.park_id || null,
              park_name: item.park_name || null,
              notes: item.notes || null,
              area: item.area ?? null,
              grass_area: item.grass_area ?? null,
              is_completed: false
            });
          }
        });
      });

      // Boş günler olabilir - sorun yok
      // En az bir faaliyet olması da gerekmez (opsiyonel kontrol)
      
      setLoading(true);

      const _startD = toLocalDateOnly(weekStartDate);
      const weekEndStr = _startD ? fmtYmd(addDays(_startD, 6)) : weekStartDate;

      // Bölge Müh/Müdür Yard formen adına plan oluşturuyorsa
      const creatorId = (isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenId 
        ? selectedFormenId 
        : personnelId;
      const creatorName = (isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenName 
        ? selectedFormenName 
        : actualPersonnelName;

      const payload = {
        week_start_date: weekStartDate,
        week_end_date: weekEndStr,
        created_by_id: creatorId,
        created_by_name: creatorName,
        activities: allActivities
      };

      // Eğer başkası oluşturuyorsa, gerçek oluşturan bilgisini ekle
      if ((isBolgeMuhendisi || isMudurYardimcisi) && selectedFormenId) {
        payload.actual_creator_id = personnelId;
        payload.actual_creator_name = actualPersonnelName;
        payload.actual_creator_role = userRole;
      }

      const response = await axios.post(`${API}/api/activity-plans`, payload);

      if (response.data) {
        toast.success("Haftalık plan başarıyla oluşturuldu!");
        setIsCreateDialogOpen(false);
        resetForm();
        await Promise.all([loadMyPlans(), loadPendingApprovals()]);
      }
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan oluşturma hatası:", error);
      toast.error(error.response?.data?.detail || error.message || "Plan oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setWeekStartDate("");
    setSelectedFormenId("");
    setSelectedFormenName("");
    setCarryOverDays({});
    setActivities(
      DAY_NAMES.map((day) => ({
        day_name: day,
        date: "",
        items: [{
          activity_type: "",
          park_id: "",
          park_name: "",
          notes: ""
        }]
      }))
    );
  };

  const handleApprovePlan = async () => {
    try {
      setApprovalLoading(true);

      // Değişiklik var mı kontrol et
      const hasChanges = JSON.stringify(editedActivities) !== JSON.stringify(selectedPlan.activities);

      // Backend'e göndermeden önce geçici özellikleri temizle
      const cleanedActivities = editedActivities.map(act => {
        const cleaned = { ...act };
        // Geçici özellikleri kaldır
        delete cleaned._id;
        delete cleaned._isOriginal;
        delete cleaned.isNew;
        delete cleaned.currentIndex;
        delete cleaned._loc;
        return cleaned;
      });

      const response = await axios.put(`${API}/api/activity-plans/${selectedPlan.id}/approve`, {
        approver_id: personnelId,
        approver_name: actualPersonnelName,
        approver_role: userRole,
        activities: hasChanges ? cleanedActivities : null, // Temizlenmiş aktiviteler
        change_reason: changeReason || null // Genel açıklama (artık kullanılmıyor, her günde kendi var)
      });

      if (response.data.success) {
        toast.success("Plan onaylandı ve ilerletildi!");
        setIsApprovalDialogOpen(false);
        setSelectedPlan(null);
        setChangeReason("");
        setEditedActivities([]);
        // Her iki listeyi de yenile
        await Promise.all([loadPendingApprovals(), loadMyPlans()]);
      }
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan onaylama hatası:", error);
      toast.error(error.response?.data?.detail || "Plan onaylanamadı");
    } finally {
      setApprovalLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      "draft": { label: "Taslak", variant: "secondary" },
      "pending_bolge": { label: "Bölge Müh. Onayı Bekliyor", variant: "outline" },
      "pending_mudur_yard": { label: "Müdür Yard. Onayı Bekliyor", variant: "outline" },
      "pending_mudur": { label: "Müdür Onayı Bekliyor", variant: "outline" },
      "approved": { label: "Onaylandı", variant: "default" },
      "rejected": { label: "Reddedildi", variant: "destructive" }
    };
    const config = statusMap[status] || { label: status, variant: "secondary" };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const planFormenOptions = useMemo(() => {
    return [...new Map(
      myPlans
        .map(p => [p.created_by_id, { id: p.created_by_id, name: p.created_by_name }])
    ).values()]
      .filter(f => f?.id && f?.name)
      .sort((a, b) => (a.name || "").localeCompare((b.name || ""), "tr-TR", { sensitivity: "base" }));
  }, [myPlans]);

  const getPresetDateRange = (preset) => {
    const today = new Date();
    const monday = getWeekMonday(today);
    if (preset === "this_week") return { start: monday, end: addDays(monday, 6) };
    if (preset === "next_week") {
      const nextMonday = addDays(monday, 7);
      return { start: nextMonday, end: addDays(nextMonday, 6) };
    }
    if (preset === "following_week") {
      const followingMonday = addDays(monday, 14);
      return { start: followingMonday, end: addDays(followingMonday, 6) };
    }
    return { start: null, end: null };
  };

  const filteredMyPlans = useMemo(() => {
    const hasManualRange = !!(appliedPlanFilters.rangeStart && appliedPlanFilters.rangeEnd);
    const { start, end } = hasManualRange
      ? { start: toLocalDateOnly(appliedPlanFilters.rangeStart), end: toLocalDateOnly(appliedPlanFilters.rangeEnd) }
      : getPresetDateRange(appliedPlanFilters.timePreset);

    return myPlans.filter((plan) => {
      const weekStart = toLocalDateOnly(plan.week_start_date);
      if (!weekStart) return false;

      if (start && weekStart < start) return false;
      if (end && weekStart > end) return false;

      if (appliedPlanFilters.formenIds.length > 0 && !appliedPlanFilters.formenIds.includes(plan.created_by_id)) return false;

      return true;
    });
  }, [
    myPlans,
    appliedPlanFilters
  ]);

  const applyPlanFilters = () => {
    if ((planRangeStart && !planRangeEnd) || (!planRangeStart && planRangeEnd)) {
      toast.error("Tarih aralığı için başlangıç ve bitiş tarihini birlikte seçin");
      return;
    }
    if (planRangeStart && planRangeEnd) {
      if (new Date(planRangeStart) > new Date(planRangeEnd)) {
        toast.error("Başlangıç tarihi bitiş tarihinden büyük olamaz");
        return;
      }
    }

    setAppliedPlanFilters({
      timePreset: planTimePreset,
      rangeStart: planRangeStart,
      rangeEnd: planRangeEnd,
      formenIds: [...planMultiFormenIds]
    });
  };

  const resetPlanFilters = () => {
    setPlanTimePreset("next_week");
    setPlanRangeStart("");
    setPlanRangeEnd("");
    setPlanMultiFormenIds([]);
    setAppliedPlanFilters({
      timePreset: "next_week",
      rangeStart: "",
      rangeEnd: "",
      formenIds: []
    });
  };

  return (
    <TabsContent value="activity-management" className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Faaliyet Yönetimi</h2>
        {has('faaliyet.olustur') && (
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Haftalık Faaliyet Planı Oluştur
          </Button>
        )}
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-3">
          {(isFormen || isBolgeMuhendisi || isMudurYardimcisi || isMudur || isGolgeAdmin) && (
            <TabsTrigger value="my-plans">
              {isFormen ? "Planlarım" : "Planlar"} ({myPlans.length})
            </TabsTrigger>
          )}
          {(isBolgeMuhendisi || isMudurYardimcisi || isMudur || isGolgeAdmin) && (
            <TabsTrigger value="approvals">
              Onay Bekleyenler ({pendingApprovals.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="history">
            Geçmiş/Envanter
          </TabsTrigger>
        </TabsList>

        {/* Planlarım */}
        <SubTabsContent value="my-plans">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Haftalık {isFormen ? "Planlarım" : "Planlar"}</CardTitle>
                <CardDescription>
                  {isFormen
                    ? "Oluşturduğunuz ve onay bekleyen haftalık faaliyet planları"
                    : "Sistemdeki tüm haftalık faaliyet planları"}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 border rounded-lg bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Hızlı Tarih Filtresi</Label>
                    <SearchableSelect
                      value={planTimePreset}
                      onChange={setPlanTimePreset}
                      className="mt-1"
                      showAllOption={false}
                      placeholder="Dönem seçin..."
                      searchPlaceholder="Dönem ara..."
                      options={PLAN_TIME_PRESETS.map(preset => ({ id: preset.value, name: preset.label }))}
                    />
                  </div>

                  {(isBolgeMuhendisi || isMudurYardimcisi || isMudur || isGolgeAdmin) && (
                    <div className="md:col-span-3">
                      <Label className="text-xs text-gray-600">Formen (Çoklu Seçim)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-1 w-full justify-between"
                        onClick={() => setIsFormenFilterOpen(prev => !prev)}
                      >
                        <span>
                          {planMultiFormenIds.length > 0
                            ? `${planMultiFormenIds.length} formen seçili`
                            : "Tüm Formenler"}
                        </span>
                        <span>{isFormenFilterOpen ? "▲" : "▼"}</span>
                      </Button>
                      {isFormenFilterOpen && (
                        <div className="mt-2 max-h-32 overflow-y-auto border rounded-md bg-white p-2 space-y-1">
                          {planFormenOptions.map(formen => {
                            const checked = planMultiFormenIds.includes(formen.id);
                            return (
                              <label key={formen.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setPlanMultiFormenIds((prev) => {
                                      if (e.target.checked) return [...new Set([...prev, formen.id])];
                                      return prev.filter(id => id !== formen.id);
                                    });
                                  }}
                                />
                                <span>{formen.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <Label className="text-xs text-gray-600">Tarih Aralığı (Opsiyonel)</Label>
                  <div className="mt-1">
                    <DateRangePicker
                      startDate={planRangeStart}
                      endDate={planRangeEnd}
                      onChange={({ start, end }) => {
                        setPlanRangeStart(start);
                        setPlanRangeEnd(end);
                      }}
                      placeholder="Tarih aralığı (opsiyonel)"
                      highlightHolidays
                      manualInput
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-600">
                    Filtre sonucu: <b>{filteredMyPlans.length}</b> / {myPlans.length} plan
                  </span>
                  <div className="flex items-center gap-2">
                    <Button type="button" onClick={applyPlanFilters}>
                      Ara
                    </Button>
                    <Button type="button" variant="outline" onClick={resetPlanFilters}>
                      Filtreleri Temizle
                    </Button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Yükleniyor...</p>
                </div>
              ) : myPlans.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">Henüz plan oluşturmadınız</p>
                  <p className="text-sm">Yeni plan oluşturmak için yukarıdaki butona tıklayın</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMyPlans.length === 0 ? (
                    <div className="text-center py-10 text-gray-500 border rounded-lg bg-white">
                      <p className="text-base font-medium">Seçili filtreye uygun plan bulunamadı</p>
                      <p className="text-sm mt-1">Filtreleri değiştirin veya temizleyin.</p>
                    </div>
                  ) : filteredMyPlans.map(plan => (
                    <Card key={plan.id} className="border">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg">
                              {plan.week_start_date} - {plan.week_end_date}
                            </CardTitle>
                            <CardDescription>
                              {/* Planın kime ait olduğunu ve kim tarafından oluşturulduğunu göster */}
                              {plan.actual_creator_id ? (
                                // Biri başkası adına oluşturdu
                                <>
                                  <div className="font-medium text-gray-700">
                                    {plan.created_by_role?.toUpperCase() || "FORMEN"} {plan.created_by_name} için
                                  </div>
                                  <div className="mt-1">
                                    <span className="inline-block px-2 py-1 bg-blue-50 border border-blue-300 rounded text-blue-700 text-sm font-medium">
                                      {plan.actual_creator_role?.replace('_', ' ').toUpperCase() || ""} {plan.actual_creator_name || "Yönetici"} tarafından {formatTurkeyTime(plan.created_at)} tarihinde oluşturuldu
                                    </span>
                                  </div>
                                </>
                              ) : (
                                // Kendi adına oluşturdu
                                <div>
                                  {plan.created_by_role?.toUpperCase() || "FORMEN"} {plan.created_by_name} tarafından {formatTurkeyTime(plan.created_at)} tarihinde oluşturuldu
                                </div>
                              )}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(plan.status)}
                            {/* Müdür ve Müdür Yardımcısı tüm planları silebilir; Formen/Bölge Müh. sadece onaylanmamış planları silebilir; Başkan/GKAGÖLGE sadece görüntüleme */}
                            {(plan.status !== "approved" || isAdmin || isMudur || isMudurYardimcisi) && !isGolgeAdmin && (
                              <Button 
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="gap-1"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDeleteConfirmDialog({ open: true, planId: plan.id, fromTab: "myplans" });
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Sil</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {(() => {
                            // Günlere göre grupla
                            const groupedByDay = {};
                            plan.activities.forEach(activity => {
                              if (!groupedByDay[activity.day_name]) {
                                groupedByDay[activity.day_name] = [];
                              }
                              groupedByDay[activity.day_name].push(activity);
                            });

                            return Object.keys(groupedByDay).map((dayName, dayIdx) => {
                              const dayActivities = groupedByDay[dayName];
                              const date = dayActivities[0].date;
                              
                              // Bu gün için değişiklikleri bul
                              const dayChanges = plan.changes?.filter(change => 
                                change.new_date === date || change.old_date === date
                              ) || [];
                              
                              return (
                                <div key={dayIdx} className="border rounded-lg p-3 bg-gray-50">
                                  <div className="font-semibold text-gray-900 mb-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span>{dayName}</span>
                                      <span className="text-sm font-normal text-gray-500">({date})</span>
                                    </div>
                                    {(() => {
                                      const resolved = dayActivities.map(a => ({ ...a, ...resolveActivityArea(a) }));
                                      const { area, grass } = computeCimBicimiTotals(resolved);
                                      if (!area && !grass) return null;
                                      const txt = [
                                        area ? `Toplam Alan: ${area} m²` : null,
                                        grass ? `Toplam Çim Alanı: ${grass} m²` : null,
                                      ].filter(Boolean).join(" · ");
                                      return (
                                        <span className="text-xs font-medium text-emerald-700">📐 {txt}</span>
                                      );
                                    })()}
                                  </div>
                                  
                                  <div className="space-y-2">
                                    {dayActivities.map((activity, actIdx) => (
                                      <div key={actIdx} className="flex items-center justify-between p-2 border rounded bg-white">
                                        <div className="flex-1">
                                          <div className="flex items-start gap-2">
                                            <span className="text-gray-400 mt-1">•</span>
                                            <div className="flex-1">
                                              <span className="text-blue-600 font-medium">{activity.activity_type}</span>
                                              {activity.park_name && (
                                                <span className="text-gray-700"> - {activity.park_name}</span>
                                              )}
                                              {(() => {
                                                const txt = formatAreaInfo(resolveActivityArea(activity));
                                                return txt ? (
                                                  <div className="text-xs text-emerald-700 mt-0.5">📐 {txt}</div>
                                                ) : null;
                                              })()}
                                              {activity.completion_notes && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                  Not: {activity.completion_notes}
                                                </div>
                                              )}
                                              {Array.isArray(activity.media) && activity.media.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                  {activity.media.map((m, mi) => (
                                                    <button
                                                      key={mi}
                                                      type="button"
                                                      onClick={() => setLightbox({ open: true, items: activity.media, index: mi })}
                                                      className="relative block w-14 h-14 rounded border overflow-hidden bg-black"
                                                      title={m.type === "video" ? "Videoyu büyüt" : "Fotoğrafı büyüt"}
                                                    >
                                                      {m.type === "photo" ? (
                                                        <img src={mediaUrl(API, m.url)} alt="" className="w-full h-full object-cover" />
                                                      ) : (
                                                        <>
                                                          <video src={mediaUrl(API, m.url)} className="w-full h-full object-cover" muted />
                                                          <span className="absolute inset-0 flex items-center justify-center text-white text-lg">▶</span>
                                                        </>
                                                      )}
                                                    </button>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        {activity.completed_at ? (
                                          // Aktivite işaretlenmiş (tamamlandı veya tamamlanamadı) + geri al
                                          <div className="flex items-center gap-2">
                                            {activity.is_completed ? (
                                              <div className="flex items-center gap-1 text-green-600">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="text-xs">Tamamlandı</span>
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-1 text-red-600">
                                                <XCircle className="h-5 w-5" />
                                                <span className="text-xs">Tamamlanamadı</span>
                                              </div>
                                            )}
                                            {plan.status === "approved" && isFormen && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900"
                                                onClick={() => handleResetActivity(plan, activity)}
                                                title="İşareti geri al"
                                              >
                                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                                Geri Al
                                              </Button>
                                            )}
                                          </div>
                                        ) : plan.status === "approved" && isFormen ? (
                                          <div className="flex gap-1">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="text-green-600 border-green-600 hover:bg-green-50"
                                              onClick={() => handleMarkCompleted(plan, activity)}
                                            >
                                              <Check className="h-4 w-4 mr-1" />
                                              Tamamlandı
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="text-red-600 border-red-600 hover:bg-red-50"
                                              onClick={async () => { await fetchMediaSettings(); openCompletionDialog(plan, activity, false); }}
                                            >
                                              <X className="h-4 w-4 mr-1" />
                                              Tamamlanamadı
                                            </Button>
                                          </div>
                                        ) : plan.status === "approved" ? (
                                          <Clock className="h-5 w-5 text-amber-500" />
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Bu gün için değişiklikler varsa göster */}
                                  {dayChanges.length > 0 && (
                                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                      <div className="text-xs font-medium text-amber-900 mb-2">
                                        📝 Yapılan Değişiklikler:
                                      </div>
                                      {dayChanges.map((change, chIdx) => (
                                        <div key={chIdx} className="mb-2 last:mb-0">
                                          <div className="text-xs text-gray-700">
                                            {change.old_park_name ? (
                                              <>
                                                <div className="font-medium text-gray-900">
                                                  Değişiklik: {change.old_park_name} → {change.new_park_name}
                                                </div>
                                                <div className="text-gray-600 mt-1">
                                                  ({change.old_activity_type} → {change.new_activity_type})
                                                </div>
                                              </>
                                            ) : (
                                              <div className="font-medium text-green-800">
                                                ✨ Yeni Faaliyet Eklendi: {change.new_activity_type} @ {change.new_park_name}
                                              </div>
                                            )}
                                            <div className="text-amber-700 mt-1 italic">
                                              {change.changed_by_name} {change.old_park_name ? 'değişiklik yaptı' : 'ekledi'}
                                            </div>
                                            {change.reason && (
                                              <div className="mt-2 p-2 bg-white border border-amber-300 rounded">
                                                <span className="font-medium text-amber-900">💬 Açıklama:</span>
                                                <span className="text-gray-700 ml-1">{change.reason}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </SubTabsContent>

        {/* Onay Bekleyenler */}
        <SubTabsContent value="approvals">
          <Card>
            <CardHeader>
              <CardTitle>Onay Bekleyen Planlar</CardTitle>
              <CardDescription>
                Sizin onayınızı bekleyen haftalık faaliyet planları
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                </div>
              ) : pendingApprovals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">Onay bekleyen plan yok</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingApprovals.map(plan => (
                    <Card key={plan.id} className="border border-amber-200 bg-amber-50">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">
                              {plan.created_by_name} - {plan.week_start_date} / {plan.week_end_date}
                            </CardTitle>
                            <CardDescription>
                              {/* Kim tarafından oluşturuldu */}
                              {plan.actual_creator_id ? (
                                <div className="mt-1">
                                  <span className="inline-block px-2 py-1 bg-blue-50 border border-blue-300 rounded text-blue-700 text-xs font-medium">
                                    {plan.actual_creator_role?.replace('_', ' ').toUpperCase() || ""} {plan.actual_creator_name} tarafından {formatTurkeyTime(plan.created_at)} tarihinde oluşturuldu
                                  </span>
                                </div>
                              ) : (
                                <div>
                                  {plan.created_by_role?.toUpperCase() || "FORMEN"} {plan.created_by_name} tarafından {formatTurkeyTime(plan.created_at)} tarihinde oluşturuldu
                                </div>
                              )}
                            </CardDescription>
                          </div>
                          {getStatusBadge(plan.status)}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 mb-4">
                          {(() => {
                            // Günlere göre grupla
                            const groupedByDay = {};
                            plan.activities.forEach(activity => {
                              if (!groupedByDay[activity.day_name]) {
                                groupedByDay[activity.day_name] = [];
                              }
                              groupedByDay[activity.day_name].push(activity);
                            });

                            return Object.keys(groupedByDay).map((dayName, dayIdx) => {
                              const dayActivities = groupedByDay[dayName];
                              const dayDate = dayActivities[0].date;
                              
                              // Bu gün için değişiklikleri bul
                              const dayChanges = plan.changes?.filter(change => 
                                change.new_date === dayDate || change.old_date === dayDate
                              ) || [];

                              return (
                                <div key={dayIdx} className="border rounded-lg p-3 bg-white">
                                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                    <span>{dayName}</span>
                                    <span className="text-sm font-normal text-gray-500">
                                      ({dayDate})
                                    </span>
                                  </div>
                                  <div className="space-y-1 pl-4">
                                    {dayActivities.map((activity, actIdx) => (
                                      <div key={actIdx} className="text-sm flex items-start gap-2">
                                        <span className="text-gray-400">•</span>
                                        <div className="flex-1">
                                          <span className="text-blue-600 font-medium">{activity.activity_type}</span>
                                          {activity.park_name && (
                                            <span className="text-gray-700"> - {activity.park_name}</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Bu gün için değişiklikler varsa göster */}
                                  {dayChanges.length > 0 && (
                                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                      <div className="text-xs font-medium text-amber-900 mb-2">
                                        📝 Değişiklikler:
                                      </div>
                                      {dayChanges.map((change, chIdx) => (
                                        <div key={chIdx} className="mb-3 last:mb-0">
                                          <div className="text-xs text-gray-700">
                                            {change.old_park_name ? (
                                              <>
                                                <div className="font-medium text-gray-900">
                                                  Değişiklik: {change.old_park_name} → {change.new_park_name}
                                                </div>
                                                <div className="text-gray-600 mt-1">
                                                  ({change.old_activity_type} → {change.new_activity_type})
                                                </div>
                                              </>
                                            ) : (
                                              <div className="font-medium text-green-800">
                                                ✨ Yeni Faaliyet Eklendi: {change.new_activity_type} @ {change.new_park_name}
                                              </div>
                                            )}
                                            <div className="text-amber-700 mt-1 italic text-xs">
                                              {change.changed_by_name} {change.old_park_name ? 'değişiklik yaptı' : 'ekledi'}
                                            </div>
                                            {change.reason && (
                                              <div className="mt-2 p-2 bg-white border border-amber-300 rounded text-xs">
                                                <span className="font-medium text-amber-900">💬 Açıklama:</span>
                                                <span className="text-gray-700 ml-1">{change.reason}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                        {!isGolgeAdmin && (
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => {
                                setSelectedPlan(plan);
                                // Deep copy ve her aktiviteye ARTAN SAYI ID ekle
                                const activitiesWithIds = JSON.parse(JSON.stringify(plan.activities || [])).map((act, idx) => ({
                                  ...act,
                                  _id: idx, // Basit sayı ID
                                  _isOriginal: true // Orijinal aktivite işareti
                                }));
                                setEditedActivities(activitiesWithIds);
                                setIsApprovalDialogOpen(true);
                              }}
                              size="sm"
                              variant="default"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Onayla
                            </Button>
                            <Button 
                              onClick={() => {
                                setSelectedPlan(plan);
                                // Deep copy ve her aktiviteye ARTAN SAYI ID ekle
                                const activitiesWithIds = JSON.parse(JSON.stringify(plan.activities || [])).map((act, idx) => ({
                                  ...act,
                                  _id: idx, // Basit sayı ID
                                  _isOriginal: true // Orijinal aktivite işareti
                                }));
                                setEditedActivities(activitiesWithIds);
                                setIsApprovalDialogOpen(true);
                              }}
                              variant="outline"
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Düzenle
                            </Button>
                            <Button 
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="gap-1"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDeleteConfirmDialog({ open: true, planId: plan.id, fromTab: "approvals" });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Sil</span>
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </SubTabsContent>

        {/* Geçmiş/Envanter - Faaliyet Raporu */}
        <SubTabsContent value="history">
          <ActivityReport 
            personnelId={personnelId}
            userRole={userRole}
            parks={parks}
            personnel={personnel}
            API={API}
          />
        </SubTabsContent>
      </Tabs>

      {/* Plan Oluşturma Kartı — park detayı kartı gibi sürüklenebilir (DraggableModal) */}
      <DraggableModal
        isOpen={isCreateDialogOpen}
        onClose={() => { setIsCreateDialogOpen(false); resetForm(); setCarryOverItems([]); }}
        title="Haftalık Faaliyet Planı Oluştur"
        subtitle="Gelecek hafta için 7 günlük faaliyet planınızı oluşturun"
        maxWidth="max-w-4xl"
        closeOnOverlayClick={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setIsCreateDialogOpen(false); resetForm(); setCarryOverItems([]); }}
            >
              İptal
            </Button>
            <Button onClick={handleCreatePlan} disabled={loading}>
              {loading ? "Oluşturuluyor..." : "Planı Oluştur"}
            </Button>
          </div>
        }
      >
          <div className="space-y-4">
            {/* Bölge Müh/Müdür Yard için formen seçimi */}
            {(isBolgeMuhendisi || isMudurYardimcisi) && (
              <div>
                <Label>Formen Seçin (Bu plan kimin adına oluşturulacak?)</Label>
                <SearchableSelect
                  value={selectedFormenId}
                  onChange={(value) => {
                    const formen = myFormens.find(f => f.id === value);
                    setSelectedFormenId(value);
                    setSelectedFormenName(formen?.name || "");
                  }}
                  className="mt-1"
                  showAllOption={false}
                  placeholder="Formen seçin..."
                  searchPlaceholder="Formen ara..."
                  options={myFormens.map(formen => ({ id: formen.id, name: formen.name }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Seçtiğiniz formen bu planı kendi panelinde görecek
                </p>
              </div>
            )}

            <DatePicker
              label="Hafta Başlangıç Tarihi (Pazartesi)"
              value={weekStartDate}
              onChange={setWeekStartDate}
            />

            {/* Geçen haftalardan devreden (Tamamlanamadı) faaliyetler */}
            {weekStartDate && carryOverItems.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-900">
                    Geçen haftalardan yapılamayan faaliyetler ({carryOverItems.length})
                  </span>
                </div>
                <p className="text-xs text-amber-700 mb-3">
                  Önceki haftalarda "Tamamlanamadı" olarak işaretlenip henüz tekrar yapılmamış faaliyetler.
                  Bu haftaya eklemek için bir gün seçip "Ekle"ye basın.
                </p>
                <div className="space-y-2">
                  {carryOverItems.map((item) => (
                    <div key={item._key} className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-white px-3 py-2">
                      <div className="flex-1 min-w-[180px]">
                        <span className="text-blue-600 font-medium text-sm">{item.activity_type}</span>
                        {item.park_name && <span className="text-gray-700 text-sm"> - {item.park_name}</span>}
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {item.date} · {item.week_start_date} haftası
                          {item.completion_notes ? ` · Açıklama: ${item.completion_notes}` : ""}
                        </div>
                      </div>
                      <div className="w-36">
                        <SearchableSelect
                          value={carryOverDays[item._key] || DAY_NAMES[0]}
                          onChange={(value) => setCarryOverDays(prev => ({ ...prev, [item._key]: value }))}
                          showAllOption={false}
                          placeholder="Gün seçin..."
                          searchPlaceholder="Gün ara..."
                          options={DAY_NAMES.map(d => ({ id: d, name: d }))}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-amber-800 border-amber-300 hover:bg-amber-100"
                        onClick={() => addCarryOverToDay(item, carryOverDays[item._key] || DAY_NAMES[0])}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ekle
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Günlük Faaliyetler</Label>
              {activities.map((day, dayIndex) => (
                <Card key={dayIndex} className="p-4 border-2">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <Label className="text-base font-bold">{day.day_name}</Label>
                      <p className="text-xs text-gray-500">{day.date}</p>
                      {(() => {
                        const { area, grass } = computeCimBicimiTotals(day.items);
                        if (!area && !grass) return null;
                        const txt = [
                          area ? `Toplam Alan: ${area} m²` : null,
                          grass ? `Toplam Çim Alanı: ${grass} m²` : null,
                        ].filter(Boolean).join(" · ");
                        return (
                          <p className="text-xs text-emerald-700 font-medium mt-0.5">📐 {txt}</p>
                        );
                      })()}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newActivities = [...activities];
                        newActivities[dayIndex].items.push({
                          activity_type: "",
                          park_id: "",
                          park_name: "",
                          notes: ""
                        });
                        setActivities(newActivities);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Faaliyet Ekle
                    </Button>
                  </div>
                  
                  {(createWeekOps[day.date] || []).length > 0 && (
                    <div className="mb-3 rounded-md border border-green-300 bg-green-50 px-3 py-2">
                      <div className="text-xs font-semibold text-green-800 mb-1">📋 Bu güne gelen operasyon emirleri (plan dışı)</div>
                      <div className="space-y-1">
                        {createWeekOps[day.date].map(o => (
                          <div key={o.id} className="text-xs text-gray-700 flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{o.title}</span>
                            {o.location_label && <span className="text-gray-500">📍 {o.location_label}{o.location_count > 1 ? ` +${o.location_count - 1}` : ""}</span>}
                            <span className="text-gray-400">· {o.created_by_name || "—"}</span>
                            <Button type="button" size="sm" variant="outline"
                              className="h-6 px-2 py-0 text-[11px] text-green-800 border-green-300 hover:bg-green-100"
                              onClick={() => setViewOpId(o.id)}>
                              Detay
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="text-[11px] text-green-700 mt-1">Bu emirleri dikkate alarak günü planlayabilirsiniz; haftalık planın yerine geçmez.</div>
                    </div>
                  )}

                  {day.items.map((item, itemIndex) => {
                    const locType = getLocItemType(item);
                    const isBolgeGeneli = item.activity_type === "Bölge Geneli Mobil Temizlik";
                    const setItem = (mutator) => {
                      const newActivities = [...activities];
                      mutator(newActivities[dayIndex].items[itemIndex]);
                      setActivities(newActivities);
                    };
                    const areaInfo = formatAreaInfo(item);
                    return (
                    <div key={itemIndex} className="mb-3 pb-3 border-b last:border-b-0 last:mb-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <Label className={itemIndex > 0 ? "text-xs font-semibold text-blue-600" : "text-xs text-gray-400"}>
                          Faaliyet #{itemIndex + 1}
                        </Label>
                        {day.items.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              const newActivities = [...activities];
                              newActivities[dayIndex].items.splice(itemIndex, 1);
                              setActivities(newActivities);
                            }}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Kaldır
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Faaliyet Tipi</Label>
                          <SearchableSelect
                            value={item.activity_type}
                            onChange={(value) => setItem(it => {
                              it.activity_type = value;
                              if (value === "Bölge Geneli Mobil Temizlik") {
                                it._loc = ""; it.park_id = ""; it.park_name = "";
                                it.area = null; it.grass_area = null;
                              }
                            })}
                            className="mt-1"
                            showAllOption={false}
                            placeholder="Seçin..."
                            searchPlaceholder="Faaliyet ara..."
                            options={ACTIVITY_TYPES.map(type => ({ id: type, name: type }))}
                          />
                        </div>

                        {!isBolgeGeneli && (
                          <div>
                            <Label className="text-xs">Alan Türü</Label>
                            <SearchableSelect
                              value={locType}
                              onChange={(value) => setItem(it => {
                                it._loc = value;
                                it.park_id = ""; it.park_name = "";
                                it.area = null; it.grass_area = null;
                                if (value === "kayitsiz_pasif") it.park_id = "KAYITSIZ_PASIF";
                                else if (value === "bolge_disi") it.park_id = "BOLGE_DISI";
                              })}
                              disabled={!item.activity_type}
                              showAllOption={false}
                              className="mt-1"
                              placeholder="Tür seçin..."
                              searchPlaceholder="Tür ara..."
                              options={LOCATION_TYPES}
                            />
                          </div>
                        )}
                      </div>

                      {/* Alan türüne göre seçim / serbest giriş + alan ölçüsü */}
                      {!isBolgeGeneli && item.activity_type && locType && (
                        <div className="mt-3">
                          {locType === "park" && (
                            <>
                              <Label className="text-xs">Park Seçin (kendi parklarınız)</Label>
                              <SearchableSelect
                                value={item.park_id}
                                onChange={(value) => setItem(it => {
                                  const p = myParks.find(x => x.id === value);
                                  it.park_id = value; it.park_name = p?.name || "";
                                  it.area = p?.area ?? null; it.grass_area = p?.grass_area ?? null;
                                })}
                                showAllOption={false}
                                className="mt-1"
                                placeholder="Park seçin..."
                                searchPlaceholder="Park ara..."
                                options={myParks.map(p => ({ id: p.id, name: p.name }))}
                              />
                            </>
                          )}
                          {locType === "pasif" && (
                            <>
                              <Label className="text-xs">Kayıtlı Pasif Alan (bölgenizdeki)</Label>
                              <SearchableSelect
                                value={item.park_id}
                                onChange={(value) => setItem(it => {
                                  const a = myPassiveAreas.find(x => `pasif:${x.id}` === value);
                                  it.park_id = value; it.park_name = a?.name || "";
                                  it.area = a?.area ?? null; it.grass_area = null;
                                })}
                                showAllOption={false}
                                className="mt-1"
                                placeholder="Pasif alan seçin..."
                                searchPlaceholder="Pasif alan ara..."
                                options={myPassiveAreas.map(a => ({ id: `pasif:${a.id}`, name: a.name }))}
                              />
                            </>
                          )}
                          {locType === "protokol" && (
                            <>
                              <Label className="text-xs">Protokollü Park (bölgenizdeki)</Label>
                              <SearchableSelect
                                value={item.park_id}
                                onChange={(value) => setItem(it => {
                                  const p = myProtokolParks.find(x => `protokol:${x.id}` === value);
                                  it.park_id = value; it.park_name = p?.name || "";
                                  it.area = p?.area ?? null; it.grass_area = p?.grass_area ?? null;
                                })}
                                showAllOption={false}
                                className="mt-1"
                                placeholder="Protokollü park seçin..."
                                searchPlaceholder="Protokollü park ara..."
                                options={myProtokolParks.map(p => ({ id: `protokol:${p.id}`, name: p.name }))}
                              />
                            </>
                          )}
                          {locType === "kayitsiz_pasif" && (
                            <>
                              <Label className="text-xs">Kayıtsız Pasif Alan (not düşün)</Label>
                              <Input
                                type="text"
                                value={item.park_name || ""}
                                onChange={(e) => setItem(it => { it.park_name = e.target.value; })}
                                placeholder="Örn: 1450 Sokak yanındaki arazi"
                                className="mt-1"
                              />
                            </>
                          )}
                          {locType === "bolge_disi" && (
                            <>
                              <Label className="text-xs">Bölge Dışı (açıklama yazın)</Label>
                              <Input
                                type="text"
                                value={item.park_name || ""}
                                onChange={(e) => setItem(it => { it.park_name = e.target.value; })}
                                placeholder="Örn: Ali Veli formen'e yardım — X parkı"
                                className="mt-1"
                              />
                            </>
                          )}

                          {areaInfo && (
                            <div className="mt-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block">
                              📐 {areaInfo}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-3">
                        <Label className="text-xs">Not (opsiyonel)</Label>
                        <Textarea
                          value={item.notes || ""}
                          onChange={(e) => setItem(it => { it.notes = e.target.value; })}
                          placeholder="İsteğe bağlı not..."
                          rows={2}
                          title={item.notes || ""}
                          className="mt-1 min-h-[40px] text-sm resize-y"
                          disabled={!item.activity_type}
                        />
                      </div>
                    </div>
                    );
                  })}
                </Card>
              ))}
            </div>
          </div>

      </DraggableModal>

      {/* Operasyon emri detayı (salt-okunur) — plan ekranından açılır */}
      {viewOpId && (
        <OperationDetailViewer API={API} orderId={viewOpId} onClose={() => setViewOpId(null)} />
      )}

      {/* Tam ekran medya görüntüleyici */}
      {lightbox.open && (
        <MediaLightbox
          items={lightbox.items}
          index={lightbox.index}
          apiBase={API}
          onClose={() => setLightbox({ open: false, items: [], index: 0 })}
          onNavigate={(i) => setLightbox(prev => ({ ...prev, index: i }))}
        />
      )}

      {/* Onay/Değiştir Dialog'u */}
      <Dialog open={isApprovalDialogOpen} onOpenChange={setIsApprovalDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plan Onaylama</DialogTitle>
            <DialogDescription>
              {selectedPlan?.created_by_name} tarafından oluşturulan haftalık planı inceleyin ve onaylayın
              {selectedPlan?.actual_creator_name && (
                <span className="ml-2 text-blue-600 font-medium">
                  (Gerçekte: {selectedPlan.actual_creator_name} tarafından oluşturuldu)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (() => {
            // Plan sahibinin parklarını hesapla
            const planOwnerParks = parks.filter(park => {
              const ownerId = selectedPlan.created_by_id;
              
              if (park.assigned_personnel_ids && park.assigned_personnel_ids.includes(ownerId)) {
                return true;
              }
              
              // Formen'in altındaki personelin parkları
              const subordinates = personnel.filter(p => p.formen_id === ownerId);
              const subordinateIds = subordinates.map(s => s.id);
              
              if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => subordinateIds.includes(id))) {
                return true;
              }

              return false;
            });

            // Onay/düzenleme için protokol park + pasif alan kapsamı:
            // plan sahibinin (formen) kapsamı veya onaylayanın görebildikleri.
            const _approveScope = (x) =>
              _seesAllAreas ||
              x.assigned_formen_id === selectedPlan.created_by_id ||
              x.assigned_bolge_muhendisi_id === personnelId ||
              x.assigned_mudur_yardimcisi_id === personnelId;
            const planOwnerProtokol = protokolParks.filter(_approveScope).sort(_byName);
            const planOwnerPasif = passiveAreas.filter(_approveScope).sort(_byName);

            return (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Hafta: {selectedPlan.week_start_date} - {selectedPlan.week_end_date}</p>
                    <p className="text-sm text-gray-600">
                      Oluşturan: {selectedPlan.created_by_name} • {formatTurkeyTime(selectedPlan.created_at)}
                    </p>
                  </div>
                  {getStatusBadge(selectedPlan.status)}
                </div>
              </div>

              {/* Operasyon emirleri overlay (Faz 4) — plan dışı, aynı güne denk gelen görevler */}
              <PlanOperationsOverlay
                API={API}
                ownerId={selectedPlan.created_by_id}
                weekStartDate={selectedPlan.week_start_date}
              />

              {/* Değişiklik geçmişi - gün bazlı gruplandırılmış */}
              {selectedPlan.changes && selectedPlan.changes.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <Label className="text-base font-semibold text-amber-900">📝 Yapılan Değişiklikler Özeti</Label>
                  </div>
                  <div className="space-y-3">
                    {(() => {
                      // Değişiklikleri tarihe göre grupla
                      const changesByDate = {};
                      selectedPlan.changes.forEach(change => {
                        const date = change.new_date || change.old_date;
                        if (!changesByDate[date]) {
                          changesByDate[date] = [];
                        }
                        changesByDate[date].push(change);
                      });
                      
                      // Her gün için göster
                      return Object.entries(changesByDate).map(([date, dayChanges]) => {
                        // Gün adını bul
                        const dayActivity = selectedPlan.activities.find(a => a.date === date);
                        const dayName = dayActivity?.day_name || "Bilinmeyen Gün";
                        
                        return (
                          <div key={date} className="bg-white border border-amber-300 rounded-lg p-3">
                            <div className="font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                              <span className="text-blue-600">{dayName}</span>
                              <span className="text-sm font-normal text-gray-500">({date})</span>
                            </div>
                            
                            <div className="space-y-3">
                              {dayChanges.map((change, idx) => (
                                <div key={idx} className="text-sm">
                                  {change.old_park_name ? (
                                    // Değişiklik
                                    <div className="space-y-1">
                                      <div className="font-medium text-gray-900">
                                        🔄 Değişiklik: {change.old_park_name} → {change.new_park_name}
                                      </div>
                                      <div className="text-gray-600 text-xs">
                                        ({change.old_activity_type} → {change.new_activity_type})
                                      </div>
                                    </div>
                                  ) : (
                                    // Yeni ekleme
                                    <div className="font-medium text-green-700">
                                      ✨ Yeni Faaliyet: {change.new_activity_type} @ {change.new_park_name}
                                    </div>
                                  )}
                                  
                                  <div className="text-xs text-amber-700 italic mt-1">
                                    {change.changed_by_name} ({change.changed_by_role}) {change.old_park_name ? 'değiştirdi' : 'ekledi'}
                                  </div>
                                  
                                  {change.reason && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                      <span className="font-medium text-amber-900">💬 Açıklama:</span>
                                      <span className="text-gray-700 ml-1">{change.reason}</span>
                                    </div>
                                  )}
                                  
                                  {idx < dayChanges.length - 1 && (
                                    <div className="border-t border-gray-200 my-2"></div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Label className="text-lg font-semibold">Planlanan Faaliyetler (Değiştirebilirsiniz)</Label>
                {editedActivities && editedActivities.length > 0 ? (
                  (() => {
                    // Aktiviteleri günlere göre grupla
                    const groupedByDay = {};
                    editedActivities.forEach((activity, idx) => {
                      if (!groupedByDay[activity.date]) {
                        groupedByDay[activity.date] = {
                          day_name: activity.day_name,
                          date: activity.date,
                          activities: []
                        };
                      }
                      // currentIndex kullan - originalIndex'i koruyalım (yeni aktivitelerde undefined)
                      groupedByDay[activity.date].activities.push({ ...activity, currentIndex: idx });
                    });

                    // Günleri tarih sırasına göre sırala (Pazartesi, Salı, Çarşamba...)
                    const sortedDays = Object.values(groupedByDay).sort((a, b) => {
                      return new Date(a.date) - new Date(b.date);
                    });

                    return sortedDays.map((dayGroup, dayIdx) => (
                      <Card key={dayIdx} className="p-4 border-2">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <Label className="text-base font-bold">{dayGroup.day_name}</Label>
                            <p className="text-xs text-gray-500">{dayGroup.date}</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Yeni ID: mevcut en büyük ID + 1
                              const maxId = Math.max(...editedActivities.map(a => a._id || 0));
                              
                              // Bu güne yeni aktivite ekle
                              const newActivity = {
                                day_name: dayGroup.day_name,
                                date: dayGroup.date,
                                activity_type: "Çim Biçimi",
                                park_id: "",
                                park_name: "",
                                notes: "",
                                is_completed: false,
                                isNew: true,  // Yeni eklenen aktivite işareti
                                _id: maxId + 1, // Yeni artan ID
                                _isOriginal: false // Yeni aktivite
                              };
                              
                              // Bu günün son aktivitesinden sonraya ekle
                              const lastActivityOfDay = dayGroup.activities[dayGroup.activities.length - 1];
                              const insertIndex = lastActivityOfDay.currentIndex + 1;
                              
                              const newActivities = [...editedActivities];
                              newActivities.splice(insertIndex, 0, newActivity);
                              
                              setEditedActivities(newActivities);
                              
                              toast.success(`${dayGroup.day_name} gününe yeni faaliyet eklendi`);
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Faaliyet Ekle
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {dayGroup.activities.map((activity, actIdx) => {
                            const currentIdx = activity.currentIndex; // editedActivities'deki gerçek index
                            
                            // Yeni eklenen aktivite mi kontrol et
                            const isNewActivity = activity.isNew === true;
                            
                            // Orijinal aktiviteyi bul - unique ID ile
                            let originalActivity = null;
                            let hasChanged = false;
                            
                            if (!isNewActivity && activity._isOriginal && activity._id !== undefined) {
                              // _id orijinal index'tir (değişmez)
                              originalActivity = selectedPlan.activities[activity._id];
                              
                              // Değişiklik kontrolü
                              if (originalActivity) {
                                hasChanged = JSON.stringify({
                                  activity_type: activity.activity_type,
                                  park_id: activity.park_id,
                                  park_name: activity.park_name
                                }) !== JSON.stringify({
                                  activity_type: originalActivity.activity_type,
                                  park_id: originalActivity.park_id,
                                  park_name: originalActivity.park_name
                                });
                              }
                            }

                            return (
                              <div key={actIdx} className="p-3 border rounded bg-white relative">
                                <div className="grid grid-cols-12 gap-3 items-start">
                                  <div className="col-span-1">
                                    <Label className="text-xs text-gray-400">#{actIdx + 1}</Label>
                                  </div>
                                  
                                  <div className="col-span-4">
                                    <Label className="text-xs">Faaliyet Tipi</Label>
                                    <SearchableSelect
                                      value={activity.activity_type}
                                      onChange={(value) => {
                                        const newActivities = [...editedActivities];
                                        newActivities[currentIdx].activity_type = value;
                                        setEditedActivities(newActivities);
                                      }}
                                      className="mt-1 h-9"
                                      showAllOption={false}
                                      searchPlaceholder="Faaliyet ara..."
                                      options={ACTIVITY_TYPES.map(type => ({ id: type, name: type }))}
                                    />
                                  </div>

                                  <div className="col-span-5">
                                    {(() => {
                                      const locType = getLocItemType(activity);
                                      const isBG = activity.activity_type === "Bölge Geneli Mobil Temizlik";
                                      const setEd = (mut) => {
                                        const na = [...editedActivities];
                                        mut(na[currentIdx]);
                                        setEditedActivities(na);
                                      };
                                      const ai = formatAreaInfo(activity);
                                      return (
                                        <>
                                          <Label className="text-xs">Alan Türü</Label>
                                          <SearchableSelect
                                            value={locType}
                                            onChange={(value) => setEd(it => {
                                              it._loc = value;
                                              it.park_id = ""; it.park_name = "";
                                              it.area = null; it.grass_area = null;
                                              if (value === "kayitsiz_pasif") it.park_id = "KAYITSIZ_PASIF";
                                              else if (value === "bolge_disi") it.park_id = "BOLGE_DISI";
                                            })}
                                            disabled={isBG}
                                            showAllOption={false}
                                            className="mt-1 h-9"
                                            placeholder="Tür seçin..."
                                            searchPlaceholder="Tür ara..."
                                            options={LOCATION_TYPES}
                                          />
                                          {!isBG && locType && (
                                            <div className="mt-1 space-y-1">
                                              {locType === "park" && (
                                                <SearchableSelect value={activity.park_id}
                                                  onChange={(value) => setEd(it => {
                                                    const p = planOwnerParks.find(x => x.id === value);
                                                    it.park_id = value; it.park_name = p?.name || "";
                                                    it.area = p?.area ?? null; it.grass_area = p?.grass_area ?? null;
                                                  })}
                                                  showAllOption={false} className="h-9" placeholder="Park seçin..." searchPlaceholder="Park ara..."
                                                  options={planOwnerParks.map(p => ({ id: p.id, name: p.name }))} />
                                              )}
                                              {locType === "pasif" && (
                                                <SearchableSelect value={activity.park_id}
                                                  onChange={(value) => setEd(it => {
                                                    const a = planOwnerPasif.find(x => `pasif:${x.id}` === value);
                                                    it.park_id = value; it.park_name = a?.name || "";
                                                    it.area = a?.area ?? null; it.grass_area = null;
                                                  })}
                                                  showAllOption={false} className="h-9" placeholder="Pasif alan seçin..." searchPlaceholder="Pasif alan ara..."
                                                  options={planOwnerPasif.map(a => ({ id: `pasif:${a.id}`, name: a.name }))} />
                                              )}
                                              {locType === "protokol" && (
                                                <SearchableSelect value={activity.park_id}
                                                  onChange={(value) => setEd(it => {
                                                    const p = planOwnerProtokol.find(x => `protokol:${x.id}` === value);
                                                    it.park_id = value; it.park_name = p?.name || "";
                                                    it.area = p?.area ?? null; it.grass_area = p?.grass_area ?? null;
                                                  })}
                                                  showAllOption={false} className="h-9" placeholder="Protokollü park seçin..." searchPlaceholder="Ara..."
                                                  options={planOwnerProtokol.map(p => ({ id: `protokol:${p.id}`, name: p.name }))} />
                                              )}
                                              {(locType === "kayitsiz_pasif" || locType === "bolge_disi") && (
                                                <Input type="text" value={activity.park_name || ""}
                                                  onChange={(e) => setEd(it => { it.park_name = e.target.value; })}
                                                  placeholder={locType === "bolge_disi" ? "Açıklama (örn: X formenine yardım)" : "Örn: 1450 Sokak yanı"}
                                                  className="h-9" />
                                              )}
                                              {ai && <div className="text-[11px] text-emerald-700">📐 {ai}</div>}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>

                                  <div className="col-span-2 flex items-end justify-end gap-2">
                                    {isNewActivity && (
                                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs">
                                        Yeni
                                      </Badge>
                                    )}
                                    {hasChanged && (
                                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">
                                        Değişti
                                      </Badge>
                                    )}
                                    {isNewActivity && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                                        onClick={() => {
                                          const newActivities = editedActivities.filter((_, idx) => idx !== currentIdx);
                                          setEditedActivities(newActivities);
                                          toast.success("Faaliyet kaldırıldı");
                                        }}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {activity.notes && (
                                  <p className="text-xs text-gray-500 mt-2">Not: {activity.notes}</p>
                                )}
                                
                                {/* Değişiklik açıklaması - sadece değişen faaliyetler için */}
                                {hasChanged && (
                                  <div className="mt-3 space-y-2">
                                    <div className="pt-2 border-t bg-yellow-50 p-2 rounded text-xs">
                                      <span className="font-semibold text-yellow-800">Değişiklik: </span>
                                      {originalActivity.park_name !== activity.park_name && (
                                        <span className="text-gray-700">
                                          {originalActivity.park_name || "Belirsiz"} → {activity.park_name || "Belirsiz"}
                                        </span>
                                      )}
                                      {originalActivity.activity_type !== activity.activity_type && (
                                        <span className="text-gray-700 ml-2">
                                          ({originalActivity.activity_type} → {activity.activity_type})
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                      <Label className="text-xs font-medium text-amber-900">
                                        💬 Bu Değişiklik İçin Açıklama (Opsiyonel)
                                      </Label>
                                      <Textarea
                                        value={activity.change_reason || ""}
                                        onChange={(e) => {
                                          const newActivities = [...editedActivities];
                                          newActivities[currentIdx].change_reason = e.target.value;
                                          setEditedActivities(newActivities);
                                        }}
                                        placeholder="Örn: Personel istedi, hava koşulları, acil durum vb."
                                        className="mt-2 bg-white text-xs"
                                        rows={2}
                                      />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Yeni faaliyet açıklaması - sadece yeni eklenen faaliyetler için */}
                                {isNewActivity && (
                                  <div className="mt-3">
                                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                      <Label className="text-xs font-medium text-green-900">
                                        💬 Yeni Faaliyet İçin Açıklama (Opsiyonel)
                                      </Label>
                                      <Textarea
                                        value={activity.change_reason || ""}
                                        onChange={(e) => {
                                          const newActivities = [...editedActivities];
                                          newActivities[currentIdx].change_reason = e.target.value;
                                          setEditedActivities(newActivities);
                                        }}
                                        placeholder="Örn: Personel talebine göre eklendi, ek görev, vb."
                                        className="mt-2 bg-white text-xs"
                                        rows={2}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    ));
                  })()
                ) : (
                  <p className="text-center text-gray-500 py-4">Bu planda faaliyet yok</p>
                )}
              </div>
            </div>
            );
          })()}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsApprovalDialogOpen(false);
                setSelectedPlan(null);
                setChangeReason("");
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleApprovePlan} 
              disabled={approvalLoading}
              size="sm"
            >
              {approvalLoading ? "Onaylanıyor..." : "Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Penceresi (Tamamlandı / Tamamlanamadı + medya) */}
      <Dialog open={showCompletionDialog} onOpenChange={(open) => {
        setShowCompletionDialog(open);
        if (!open) {
          setDialogMedia([]);
          setCompletionNote("");
          setSelectedActivity(null);
        }
      }}>
        <DialogContent>
          {(() => {
            const isCompleted = selectedActivity?.isCompleted;
            const req = mediaSettings.requirement;
            const needPhoto = req === "foto" || req === "ikisi";
            const needVideo = req === "video" || req === "ikisi";
            const showGaleri = mediaSettings.source === "galeri_ve_kamera";
            const showMedia = req !== "yok";
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{isCompleted ? "Görevi Tamamla" : "Görev Tamamlanamadı"}</DialogTitle>
                  <DialogDescription>
                    {selectedActivity?.activityType}
                    {selectedActivity?.parkName ? ` — ${selectedActivity.parkName}` : ""}
                    {isCompleted
                      ? ". Tamamlandı olarak işaretlemek üzeresiniz."
                      : ". Neden tamamlanamadı? Lütfen açıklama girin."}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">
                      Açıklama {isCompleted ? "(opsiyonel)" : "(zorunlu)"}
                    </Label>
                    <Textarea
                      placeholder={isCompleted ? "İsteğe bağlı not..." : "Örn: Yağmur yağdı, ekipman arızası, vb."}
                      value={completionNote}
                      onChange={(e) => setCompletionNote(e.target.value)}
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  {showMedia && (
                    <div className="rounded-md border p-3 bg-gray-50">
                      <Label className="text-xs font-semibold">
                        Kanıt Medyası
                        <span className="font-normal text-gray-500">
                          {" "}
                          ({!isCompleted
                            ? "opsiyonel"
                            : needPhoto && needVideo ? "fotoğraf + video zorunlu" : needPhoto ? "fotoğraf zorunlu" : "video zorunlu"}, en fazla 3)
                        </span>
                      </Label>

                      <div className="flex flex-wrap gap-2 mt-2">
                        {needPhoto && showGaleri && (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded cursor-pointer bg-white hover:bg-gray-100">
                            📷 Galeriden Foto
                            <input type="file" accept="image/*" className="hidden"
                              disabled={uploadingMedia || dialogMedia.length >= 3}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadDialogMedia(f, "photo", "galeri"); }} />
                          </label>
                        )}
                        {needPhoto && (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded cursor-pointer bg-white hover:bg-gray-100">
                            📸 Kamera ile Foto
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                              disabled={uploadingMedia || dialogMedia.length >= 3}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadDialogMedia(f, "photo", "kamera"); }} />
                          </label>
                        )}
                        {needVideo && showGaleri && (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded cursor-pointer bg-white hover:bg-gray-100">
                            🎞️ Galeriden Video
                            <input type="file" accept="video/*" className="hidden"
                              disabled={uploadingMedia || dialogMedia.length >= 3}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadDialogMedia(f, "video", "galeri"); }} />
                          </label>
                        )}
                        {needVideo && (
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded cursor-pointer bg-white hover:bg-gray-100">
                            🎥 Kamera ile Video
                            <input type="file" accept="video/*" capture="environment" className="hidden"
                              disabled={uploadingMedia || dialogMedia.length >= 3}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadDialogMedia(f, "video", "kamera"); }} />
                          </label>
                        )}
                      </div>

                      {uploadingMedia && (
                        <p className="text-xs text-gray-500 mt-2">Yükleniyor...</p>
                      )}

                      {dialogMedia.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {dialogMedia.map((m, i) => (
                            <div key={i} className="relative w-20 h-20 pt-2 pr-2">
                              <div className="w-full h-full rounded border overflow-hidden bg-black">
                                {m.type === "photo" ? (
                                  <img src={mediaUrl(API, m.url)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <video src={mediaUrl(API, m.url)} className="w-full h-full object-cover" muted />
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => setDialogMedia(prev => prev.filter((_, j) => j !== i))}
                                className="absolute top-0 right-0 z-10 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow hover:bg-red-700"
                                title="Bu medyayı kaldır"
                                aria-label="Kaldır"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {mediaSettings.source === "kamera" && (
                        <p className="text-[11px] text-gray-500 mt-2">
                          Yalnızca cihaz kamerasıyla çekim açıktır (galeriden yükleme kapalı).
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setShowCompletionDialog(false);
                    setDialogMedia([]);
                    setCompletionNote("");
                    setSelectedActivity(null);
                  }}>
                    İptal
                  </Button>
                  <Button
                    onClick={submitCompletion}
                    disabled={completionSaving || uploadingMedia}
                    className={isCompleted ? "" : "bg-red-600 hover:bg-red-700"}
                  >
                    {completionSaving ? "Kaydediliyor..." : "Kaydet"}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Silme Onay Dialogu */}
      <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => {
        if (!open) setDeleteConfirmDialog({ open: false, planId: null, fromTab: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-6 w-6" />
              Planı Silmek İstediğinizden Emin Misiniz?
            </DialogTitle>
            <DialogDescription className="pt-4 space-y-2">
              <p className="text-base font-medium text-gray-900">
                Bu işlem geri alınamaz!
              </p>
              <p className="text-sm text-gray-600">
                Plan kalıcı olarak silinecek ve tüm faaliyet bilgileri kaybolacaktır.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConfirmDialog({ open: false, planId: null, fromTab: null })}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeletePlan}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Evet, Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}

// Faz 4 — Haftalık planın yanında, aynı güne denk gelen operasyon emirlerini
// (plan dışı) gösterir. Planı DEĞİŞTİRMEZ; yalnızca bilgi amaçlı listeler.
function PlanOperationsOverlay({ API, ownerId, weekStartDate }) {
  const [byDay, setByDay] = useState({});

  useEffect(() => {
    if (!ownerId || !weekStartDate) return;
    const start = toLocalDateOnly(weekStartDate);
    if (!start) return;
    const dates = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
    let alive = true;
    Promise.all(
      dates.map(date =>
        axios.get(`${API}/api/operasyon/by-date`, { params: { personnel_id: ownerId, date } })
          .then(r => [date, Array.isArray(r.data) ? r.data : []])
          .catch(() => [date, []])
      )
    ).then(pairs => { if (alive) setByDay(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [API, ownerId, weekStartDate]);

  const dayNameOf = (dateStr) => {
    const d = toLocalDateOnly(dateStr);
    if (!d) return "";
    const js = d.getDay();
    return DAY_NAMES[js === 0 ? 6 : js - 1];
  };

  const daysWithOps = Object.entries(byDay).filter(([, ops]) => ops && ops.length > 0);
  if (daysWithOps.length === 0) return null;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-base font-semibold text-green-900">📋 Anlık Görevler (plan dışı)</Label>
      </div>
      <div className="space-y-2">
        {daysWithOps.map(([date, ops]) => (
          <div key={date} className="bg-white border border-green-200 rounded p-2">
            <div className="font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="text-green-700">{dayNameOf(date)}</span>
              <span className="text-sm font-normal text-gray-500">({date})</span>
            </div>
            <div className="space-y-1">
              {ops.map(o => (
                <div key={o.id} className="text-xs text-gray-700 flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{o.title}</span>
                  {o.location_label && <span className="text-gray-400">📍 {o.location_label}{o.location_count > 1 ? ` +${o.location_count - 1}` : ""}</span>}
                  <span className="text-gray-400">· {o.created_by_name || "—"}</span>
                  {o.is_overdue && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">Gecikti</Badge>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-green-700 mt-2">
        Bu emirler haftalık planın yerine geçmez; yalnızca aynı güne denk gelen operasyon görevlerini gösterir.
      </p>
    </div>
  );
}

export default memo(ActivityManagementTab);
